import { useState, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, writeBatch, addDoc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { checkRateLimit } from '../util/rateLimiter';
import { getRateLimit, buildGlobalRateKey } from '../util/rateLimitConfig';

/**
 * Custom hook for managing follow/unfollow functionality
 * Extracted from profile.js for reuse in mobile and web components
 */
export const useFollowSystem = (authenticatedUserHandle, authenticatedUserId, isPremiumUser) => {
  const [isFollowLoading, setIsFollowLoading] = useState(false);

  // Function to handle follow notifications
  const handleFollowNotification = useCallback(async (user, isFollowing) => {
    try {
      if (isFollowing) {
        // Add a new notification when following
        const docRef = await addDoc(collection(firestore, "notifications"), {
          type: "new_follower",
          followedBy: authenticatedUserHandle,
          followedTo: user,
          targetUser: user,
          status: "unread",
          timestamp: new Date()
        });
      } else {
        // Find and delete notification when unfollowing
        const notificationsQuery = query(
          collection(firestore, "notifications"),
          where("type", "==", "new_follower"),
          where("followedBy", "==", authenticatedUserHandle),
          where("followedTo", "==", user)
        );

        const notificationsSnapshot = await getDocs(notificationsQuery);

        if (!notificationsSnapshot.empty) {
          const batch = writeBatch(firestore);

          notificationsSnapshot.forEach(doc => {
            batch.delete(doc.ref);
          });

          await batch.commit();
        }
      }
    } catch (error) {
      console.error("Error handling follow notification:", error);
    }
  }, [authenticatedUserHandle]);

  // Function to follow a user
  const followUser = useCallback(async (targetUserHandle, targetUserId) => {
    try {
      setIsFollowLoading(true);

      if (!authenticatedUserHandle || !authenticatedUserId) {
        alert("You must be logged in to follow users");
        setIsFollowLoading(false);
        return false;
      }

      // We should already have the target user ID, if not, we'll try to find it
      let userTargetId = targetUserId;

      if (!userTargetId) {
        // Try to find the user with case-insensitive search
        const allUsersSnapshot = await getDocs(collection(firestore, "users"));
        const normalizedTargetHandle = targetUserHandle.toLowerCase();

        const matchingUser = allUsersSnapshot.docs.find(doc =>
          doc.data().handle &&
          doc.data().handle.toLowerCase() === normalizedTargetHandle
        );

        if (!matchingUser) {
          console.error("Target user not found with handle:", targetUserHandle);
          alert(`User "${targetUserHandle}" not found in the database.`);
          setIsFollowLoading(false);
          return false;
        }

        userTargetId = matchingUser.id;
      }

      if (!userTargetId) {
        console.error("Failed to determine target user ID");
        setIsFollowLoading(false);
        return false;
      }

      // Start a batch write
      const batch = writeBatch(firestore);

      // Update current user's following list
      const currentUserRef = doc(firestore, "users", authenticatedUserHandle);
      const currentUserSnap = await getDoc(currentUserRef);

      if (!currentUserSnap.exists()) {
        // Fallback: try to find the user document by querying for the handle
        const currentUserQuery = query(
          collection(firestore, "users"),
          where("handle", "==", authenticatedUserHandle)
        );

        const currentUserSnapshot = await getDocs(currentUserQuery);

        if (currentUserSnapshot.empty) {
          console.error("Current user not found even with query by handle");
          setIsFollowLoading(false);
          return false;
        }

        const currentUserDoc = currentUserSnapshot.docs[0];
        const currentUserData = currentUserDoc.data();

        const following = currentUserData.following || [];
        const normalizedTargetHandle = targetUserHandle.toLowerCase();
        const alreadyFollowing = following.some(handle =>
          handle.toLowerCase() === normalizedTargetHandle
        );

        if (!alreadyFollowing) {
          following.push(targetUserHandle);
          batch.update(doc(firestore, "users", currentUserDoc.id), { following });
        }
      } else {
        const currentUserData = currentUserSnap.data();
        const following = currentUserData.following || [];
        const normalizedTargetHandle = targetUserHandle.toLowerCase();
        const alreadyFollowing = following.some(handle =>
          handle.toLowerCase() === normalizedTargetHandle
        );

        if (!alreadyFollowing) {
          following.push(targetUserHandle);
          batch.update(currentUserRef, { following });
        }
      }

      // Update target user's followers list
      const targetUserRef = doc(firestore, "users", userTargetId);
      const targetUserSnap = await getDoc(targetUserRef);

      if (!targetUserSnap.exists()) {
        console.error("Target user document not found with ID:", userTargetId);
        setIsFollowLoading(false);
        return false;
      }

      const targetUserData = targetUserSnap.data();
      const followers = targetUserData.followers || [];
      const normalizedCurrentHandle = authenticatedUserHandle.toLowerCase();
      const alreadyInFollowers = followers.some(handle =>
        handle.toLowerCase() === normalizedCurrentHandle
      );

      if (!alreadyInFollowers) {
        followers.push(authenticatedUserHandle);
        batch.update(targetUserRef, { followers });
      }

      // Commit the batch
      await batch.commit();

      // Create a follow notification
      await handleFollowNotification(targetUserHandle, true);

      setIsFollowLoading(false);
      return true;
    } catch (error) {
      console.error("Error following user:", error);
      alert("Failed to follow user. Please try again.");
      setIsFollowLoading(false);
      return false;
    }
  }, [authenticatedUserHandle, authenticatedUserId, handleFollowNotification]);

  // Function to unfollow a user
  const unfollowUser = useCallback(async (targetUserHandle, targetUserId) => {
    try {
      setIsFollowLoading(true);

      if (!authenticatedUserHandle || !authenticatedUserId) {
        alert("You must be logged in to unfollow users");
        setIsFollowLoading(false);
        return false;
      }

      let userTargetId = targetUserId;

      if (!userTargetId) {
        const allUsersSnapshot = await getDocs(collection(firestore, "users"));
        const normalizedTargetHandle = targetUserHandle.toLowerCase();

        const matchingUser = allUsersSnapshot.docs.find(doc =>
          doc.data().handle &&
          doc.data().handle.toLowerCase() === normalizedTargetHandle
        );

        if (!matchingUser) {
          console.error("Target user not found with handle:", targetUserHandle);
          alert(`User "${targetUserHandle}" not found in the database.`);
          setIsFollowLoading(false);
          return false;
        }

        userTargetId = matchingUser.id;
      }

      if (!userTargetId) {
        console.error("Failed to determine target user ID");
        setIsFollowLoading(false);
        return false;
      }

      // Start a batch write
      const batch = writeBatch(firestore);

      // Update current user's following list
      const currentUserRef = doc(firestore, "users", authenticatedUserHandle);
      const currentUserSnap = await getDoc(currentUserRef);

      if (!currentUserSnap.exists()) {
        const currentUserQuery = query(
          collection(firestore, "users"),
          where("handle", "==", authenticatedUserHandle)
        );

        const currentUserSnapshot = await getDocs(currentUserQuery);

        if (currentUserSnapshot.empty) {
          console.error("Current user not found even with query by handle");
          setIsFollowLoading(false);
          return false;
        }

        const currentUserDoc = currentUserSnapshot.docs[0];
        const currentUserData = currentUserDoc.data();

        const normalizedTargetHandle = targetUserHandle.toLowerCase();
        let following = currentUserData.following || [];
        following = following.filter(handle => handle.toLowerCase() !== normalizedTargetHandle);
        batch.update(doc(firestore, "users", currentUserDoc.id), { following });
      } else {
        const currentUserData = currentUserSnap.data();
        const normalizedTargetHandle = targetUserHandle.toLowerCase();
        let following = currentUserData.following || [];
        following = following.filter(handle => handle.toLowerCase() !== normalizedTargetHandle);
        batch.update(currentUserRef, { following });
      }

      // Update target user's followers list
      const targetUserRef = doc(firestore, "users", userTargetId);
      const targetUserSnap = await getDoc(targetUserRef);

      if (!targetUserSnap.exists()) {
        console.error("Target user document not found with ID:", userTargetId);
        setIsFollowLoading(false);
        return false;
      }

      const targetUserData = targetUserSnap.data();
      const normalizedCurrentHandle = authenticatedUserHandle.toLowerCase();
      let followers = targetUserData.followers || [];
      followers = followers.filter(handle => handle.toLowerCase() !== normalizedCurrentHandle);
      batch.update(targetUserRef, { followers });

      // Commit the batch
      await batch.commit();

      // Delete the follow notification
      await handleFollowNotification(targetUserHandle, false);

      setIsFollowLoading(false);
      return true;
    } catch (error) {
      console.error("Error unfollowing user:", error);
      alert("Failed to unfollow user. Please try again.");
      setIsFollowLoading(false);
      return false;
    }
  }, [authenticatedUserHandle, authenticatedUserId, handleFollowNotification]);

  // Function to handle follow/unfollow button click with rate limiting
  const handleFollowClick = useCallback(async (isCurrentlyFollowing, targetUserHandle, targetUserId, onSuccess) => {
    if (isFollowLoading) return;

    try {
      if (!authenticatedUserHandle || !authenticatedUserId) {
        alert("You must be logged in to follow users");
        return;
      }

      if (!targetUserHandle) {
        alert("Unable to identify the user to follow");
        return;
      }

      // Check rate limit
      const userTier = isPremiumUser ? 'premium' : 'free';
      const config = getRateLimit('FOLLOW', userTier);
      const rateKey = buildGlobalRateKey(authenticatedUserId, 'follow');
      
      const isLimited = checkRateLimit({
        key: rateKey,
        limit: config.limit,
        windowMs: config.windowMs
      });

      if (isLimited) {
        // Rate limit exceeded - could show modal or alert
        alert("Please wait a moment and try again.");
        return;
      }

      if (isCurrentlyFollowing) {
        const success = await unfollowUser(targetUserHandle, targetUserId);
        if (success && onSuccess) {
          onSuccess(false, -1); // isFollowing: false, followersCountChange: -1
        }
      } else {
        const success = await followUser(targetUserHandle, targetUserId);
        if (success && onSuccess) {
          onSuccess(true, 1); // isFollowing: true, followersCountChange: +1
        }
      }
    } catch (error) {
      console.error("Error handling follow click:", error);
      alert("An error occurred while trying to follow/unfollow. Please try again.");
    }
  }, [isFollowLoading, authenticatedUserHandle, authenticatedUserId, isPremiumUser, followUser, unfollowUser]);

  return {
    // State
    isFollowLoading,
    
    // Functions
    followUser,
    unfollowUser,
    handleFollowClick,
    handleFollowNotification
  };
};